import React from "react";
import { Clock } from "lucide-react";

/**
 * Hero visual generato via CSS per le card blog e i banner articolo.
 * Non usa asset esterni: gradient brand + logo Simple Icons (per fornitori)
 * oppure simbolo editoriale grande (TD, RF19, NS, EST…) per articoli generici.
 *
 * Props:
 *   visual: { kind: 'brand'|'editorial', brandSlug?, color, symbol?, textColor? }
 *   readingMinutes?: number  → badge "N min" in alto a destra
 *   aspect: className tailwind (default 'aspect-[16/10]')
 *   size: 'small'|'default'|'large'  → dimensione simbolo editoriale
 */
export default function PostHeroVisual({
  visual,
  readingMinutes,
  aspect = "aspect-[16/10]",
  size = "default",
  showBadge = true,
}) {
  const kind = visual?.kind || "editorial";
  const color = visual?.color || "#1D4ED8";
  const symbol = visual?.symbol || "TD";
  const brandSlug = visual?.brandSlug;
  const textColor = visual?.textColor || "#FFFFFF";

  const bg = `linear-gradient(135deg, ${color} 0%, ${darken(color, 0.2)} 100%)`;

  const sizeCls =
    size === "small" ? "text-5xl sm:text-6xl" : size === "large" ? "text-8xl sm:text-9xl" : "text-7xl sm:text-8xl";

  return (
    <div className={`relative ${aspect} overflow-hidden`} style={{ background: bg }}>
      {/* Grain / dots pattern */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, white 1px, transparent 1.5px), radial-gradient(circle at 75% 20%, white 1px, transparent 1.5px), radial-gradient(circle at 45% 75%, white 1px, transparent 1.5px)",
          backgroundSize: "42px 42px, 58px 58px, 32px 32px",
        }}
      />
      {/* Diagonal light ribbon */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.06) 50%, transparent 60%)",
        }}
      />
      {/* Corner glow */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full"
        style={{ background: "rgba(255,255,255,0.10)", filter: "blur(30px)" }}
      />

      {/* Content */}
      <div className="absolute inset-0 grid place-items-center px-6">
        {kind === "brand" && brandSlug ? (
          <img
            src={`https://cdn.simpleicons.org/${brandSlug}/ffffff`}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="w-20 h-20 sm:w-24 sm:h-24 opacity-80 drop-shadow-[0_4px_20px_rgba(0,0,0,0.25)]"
          />
        ) : (
          <div
            className={`font-display font-black ${sizeCls} leading-none tracking-tighter opacity-45 select-none`}
            style={{ color: textColor }}
          >
            {symbol}
          </div>
        )}
      </div>

      {/* Reading time badge */}
      {showBadge && readingMinutes && (
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur text-[#0A0A0A] text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
          <Clock className="w-3 h-3" />
          {readingMinutes} min
        </div>
      )}
    </div>
  );
}

// ---- helpers -----------------------------------------------------------

function darken(hex, amount) {
  const c = String(hex || "#1D4ED8").replace("#", "");
  const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return hex;
  const delta = Math.round(255 * amount);
  const clamp = (v) => Math.max(0, Math.min(255, v - delta));
  const r = clamp((num >> 16) & 0xff);
  const g = clamp((num >> 8) & 0xff);
  const b = clamp(num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
